import { fDate } from '@/utils/dayjs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { User, Role, ShopStatus } from '@/types/user.type'
import { MoreHorizontal, Edit, Store } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

interface ShopsTableProps {
  shops: User[]
  loading: boolean
  onView?: (shop: User) => void
  onEdit?: (shop: User) => void
  onDelete?: (shop: User) => void
}

const getStatusBadge = (status: ShopStatus) => {
  const statusConfig = {
    [ShopStatus.OPEN]: {
      label: 'เปิดร้าน',
      className: 'bg-green-500 text-background',
    },
    [ShopStatus.CLOSED]: {
      label: 'ปิดร้าน',
      className: 'bg-red-500 text-background',
    },
  }

  const config = statusConfig[status] || statusConfig[ShopStatus.CLOSED]
  return <Badge className={config.className}>{config.label}</Badge>
}

const getRoleBadge = (role: Role) => {
  const roleConfig = {
    [Role.ADMIN]: { label: 'ผู้ดูแลระบบ', className: 'bg-blue-500 text-white' },
    [Role.SHOP]: { label: 'ร้านค้า', className: 'bg-green-500 text-white' },
    [Role.USER]: { label: 'ผู้ใช้', className: 'bg-gray-500 text-white' },
  }

  const config = roleConfig[role] || roleConfig[Role.SHOP]
  return <Badge className={config.className}>{config.label}</Badge>
}

export default function ShopsTable({
  shops,
  loading,
  // onView,
  onEdit,
  // onDelete,
}: ShopsTableProps) {
  if (loading) {
    return (
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">โลโก้</TableHead>
              <TableHead>ข้อมูลร้านค้า</TableHead>
              <TableHead className="w-24">ประเภท</TableHead>
              <TableHead className="w-24">สถานะ</TableHead>
              <TableHead className="w-32">วันที่อัปเดต</TableHead>
              <TableHead className="w-16">การจัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                </TableCell>
                <TableCell>
                  <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (shops.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-base text-mute">ไม่พบข้อมูลร้านค้า</p>
      </div>
    )
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">โลโก้</TableHead>
            <TableHead>ข้อมูลร้านค้า</TableHead>
            <TableHead className="w-24">ประเภท</TableHead>
            <TableHead className="w-24">สถานะ</TableHead>
            <TableHead className="w-32">วันที่อัปเดต</TableHead>
            <TableHead className="w-16">การจัดการ</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shops.map((shop) => (
            <TableRow key={shop.uid}>
              <TableCell>
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                  {shop.imageUrl ? (
                    <img
                      src={shop.imageUrl}
                      alt={shop.displayName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Store className="h-6 w-6 text-gray-400" />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <div className="space-y-1">
                    <p className="text-base font-medium text-black">
                      {shop.displayName}
                    </p>
                    <p className="text-sm text-mute">
                      เจ้าของ: {shop.displayName}
                    </p>
                    <p className="text-sm text-mute">อีเมล: {shop.email}</p>
                  </div>
                </div>
              </TableCell>
              <TableCell>{getRoleBadge(shop.role)}</TableCell>
              <TableCell>{getStatusBadge(shop.shopStatus)}</TableCell>
              <TableCell>
                <p className="text-sm text-mute">{fDate(shop.updatedAt)}</p>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* {onView && (
                      <DropdownMenuItem onClick={() => onView(shop)}>
                        <Eye className="mr-2 h-4 w-4" />
                        ดูรายละเอียด
                      </DropdownMenuItem>
                    )} */}
                    {onEdit && (
                      <DropdownMenuItem onClick={() => onEdit(shop)}>
                        <Edit className="mr-2 h-4 w-4" />
                        แก้ไข
                      </DropdownMenuItem>
                    )}
                    {/* {onDelete && (
                      <DropdownMenuItem
                        onClick={() => onDelete(shop)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        ลบ
                      </DropdownMenuItem>
                    )} */}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
